import React, { FunctionComponent, useMemo, useState } from "react";
import styles from "./styles.module.scss";
import { Grid, ListItemIcon, Menu, MenuItem, Skeleton } from "@mui/material";
import { IAlbumType, useGetAlbumTypes } from "../../../../hooks/albumTypes";
import { strings } from "../../../../localization/LocalizedStrings";
import { ModifyAlbumTypeDialog } from "../ModifyAlbumTypeDialog";
import { AttributeDesktop } from "../../AttributeDesktop";
import { toast } from "react-toastify";
import { useContextMenu } from "../../../../hooks/contextMenu";
import { attributeIconMap } from "../../../../models/AttributeUtilities";
import { AttributeType } from "../../AttributeList";
import { Icon, IconType } from "../../../../icons";
import { useResettableDialog } from "../../../../hooks/resettableDialog/useResettableDialog";
import { AlbumTypeCard } from "../AlbumTypeCard";

export const AlbumTypesDesktop: FunctionComponent<IAlbumTypesDesktopProps> = ({
  handleClose,
  isPublic,
  columnWidth,
  preselectedId,
  onAlbumTypeSelected,
  onClearValue
}) => {
  const [loading, setLoading] = useState(true);
  const [albumTypes, resetAlbumTypes, setAlbumTypes, noMoreAlbumTypes] = useGetAlbumTypes(
    isPublic,
    setLoading
  );
  const {
    state: dialogState,
    key: dialogKey,
    openAdd,
    openEdit,
    close: closeDialog
  } = useResettableDialog<IAlbumType>();
  const [searchText, setSearchText] = useState("");
  const [contextAlbumType, setContextAlbumType] = useState<IAlbumType>();
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const filteredAlbumTypes = useMemo(() => {
    if (searchText === "") {
      return albumTypes;
    }
    return albumTypes?.filter(albumType => {
      return albumType.name.toLowerCase().includes(searchText.toLowerCase());
    });
  }, [albumTypes, searchText]);
  return (
    <AttributeDesktop
      handleClose={handleClose}
      isPublic={isPublic}
      items={filteredAlbumTypes}
      emptyIconType={attributeIconMap[AttributeType.ALBUM_TYPES]}
      emptyString={isPublic ? strings.empty_album_types_public : strings.empty_album_types}
      addItemString={strings.add_attribute}
      title={strings.attribute_album_types_title}
      loading={loading}
      setLoading={setLoading}
      onSearchRequested={searchText => setSearchText(searchText)}
      addAction={openAdd}
      refreshAction={resetAlbumTypes}
      onClearValue={onClearValue}
    >
      {filteredAlbumTypes?.map(albumType => (
        <Grid item key={albumType.uuid} xs={columnWidth}>
          <AlbumTypeCard
            albumType={albumType}
            selected={preselectedId === albumType.uuid}
            onClick={() => {
              if (onAlbumTypeSelected) {
                onAlbumTypeSelected(albumType);
              } else {
                openEdit(albumType);
              }
            }}
            onContextMenu={event => {
              setContextAlbumType(albumType);
              handleContextMenu(event);
            }}
          />
        </Grid>
      ))}
      {(loading || !noMoreAlbumTypes) &&
        Array.from({ length: 6 }, (_value, index) => (
          <Grid key={index} item xs={columnWidth}>
            <Skeleton variant={"rectangular"} sx={{ height: 64 }} className={styles.skeleton} />
          </Grid>
        ))}
      <Menu {...menuProps}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openEdit(contextAlbumType);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.EditSquare} />
          </ListItemIcon>
          {strings.alert_modify}
        </MenuItem>
      </Menu>
      <ModifyAlbumTypeDialog
        key={dialogKey}
        open={dialogState.mode !== "closed"}
        onClose={closeDialog}
        albumType={dialogState.item}
        onNewItem={newAlbumType => {
          const newAlbumTypes = [...albumTypes, newAlbumType];
          newAlbumTypes.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { ignorePunctuation: true })
          );
          setAlbumTypes(newAlbumTypes);
          toast.success(strings.generic_update_success);
        }}
        onModifiedItem={newAlbumType => {
          const newAlbumTypes = albumTypes.map(albumType =>
            albumType.uuid === newAlbumType.uuid ? newAlbumType : albumType
          );
          newAlbumTypes.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { ignorePunctuation: true })
          );
          setAlbumTypes(newAlbumTypes);
          toast.success(strings.generic_update_success);
        }}
        onDeletedItem={deletedAlbumType => {
          const newAlbumTypes = albumTypes.filter(
            albumType => albumType.uuid !== deletedAlbumType.uuid
          );
          setAlbumTypes(newAlbumTypes);
          toast.success(strings.generic_update_success);
        }}
      />
    </AttributeDesktop>
  );
};

export interface IAlbumTypesDesktopProps {
  handleClose?: () => void;
  isPublic: boolean;
  columnWidth: number;
  preselectedId?: string;
  onAlbumTypeSelected?: (albumType: IAlbumType) => void;
  onClearValue?: () => void;
}
